import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, ProtectedRoute, NotificationProvider, useClientQueries } from 'shared-lib';
import { ClientContextProvider } from './context';
import { useClientContext } from './context/ClientContextProvider';
import { PropertyDrawer, BottomNavigation, PageHeader } from './components';
import Home from './pages/Home';
import Login from './pages/Login';
import Registration from './pages/Registration';
import ServiceRequests from './pages/ServiceRequests';
import NewServiceRequest from './pages/NewServiceRequest';
import ServiceRequestDetail from './pages/ServiceRequestDetail';
import './App.css';
import { Container } from '@mui/material';

// Separate the non-memoizable parts (hooks) and the JSX rendering
function useAppLogic() {
  // Keep all hooks at the top level, in the same order every time
  const { currentUser } = useAuth();
  const { currentProperty, properties } = useClientContext();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Use the client queries hook
  const { useHasClientAccess } = useClientQueries();
  const { data: hasAccess = false, isLoading: isAccessLoading } = useHasClientAccess();
  
  // Create an auth result compatible with ProtectedRoute
  const clientAuthResult = useMemo(() => ({
    currentUser,
    loading: isAccessLoading,
    isAuthorized: hasAccess,
    profiles: properties
  }), [currentUser, isAccessLoading, hasAccess, properties]);
  
  const openDrawer = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  // Open drawer when returning from registration
  // or when there are properties but none is selected
  useEffect(() => {
    const justRegistered = location.state?.from === '/register';
    
    // Only open drawer on first load if coming from registration
    // Remove the condition that opens drawer when no property is selected
    if (justRegistered) {
      setDrawerOpen(true);
      // Clear the location state
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  // Listen for custom event to open drawer
  useEffect(() => {
    const handleOpenDrawerEvent = () => {
      setDrawerOpen(true);
    };
    
    window.addEventListener('openPropertyDrawer', handleOpenDrawerEvent);
    
    return () => {
      window.removeEventListener('openPropertyDrawer', handleOpenDrawerEvent);
    };
  }, []);

  return {
    currentUser,
    drawerOpen,
    clientAuthResult,
    openDrawer,
    closeDrawer
  };
}

const AppContent = React.memo(function AppContent() {
  const {
    currentUser,
    drawerOpen,
    clientAuthResult,
    openDrawer,
    closeDrawer
  } = useAppLogic();
  
  return (
    <>
      {/* Page Header with current page name and actions */}
      <PageHeader />
      
      <Container component="main" sx={{ 
        mt: 2, 
        pb: 8,
        '& .MuiPaper-root': {
          borderRadius: '16px',
          border: '1px solid rgba(16, 29, 59, 0.2)',
          boxShadow: '0px 8px 32px -4px rgba(16, 29, 59, 0.1)'
        },
        '& .MuiButton-contained': {
          backgroundColor: '#00AB9B',
          borderRadius: '5px',
          padding: '10px 20px',
          transition: 'background-color 0.3s ease',
          '&:hover': {
            backgroundColor: '#00AB9B',
            filter: 'brightness(0.9)'
          }
        },
        '& .MuiButton-outlined': {
          borderColor: '#00AB9B',
          color: '#00AB9B',
          borderRadius: '5px',
          padding: '10px 20px',
          '&:hover': {
            borderColor: '#00AB9B',
            backgroundColor: 'rgba(0, 171, 155, 0.04)'
          }
        },
        '& .MuiTextField-root': {
          '& .MuiOutlinedInput-root': {
            borderRadius: '16px',
            '& fieldset': {
              borderColor: 'rgba(16, 29, 59, 0.2)'
            },
            '&:hover fieldset': {
              borderColor: 'rgba(16, 29, 59, 0.3)'
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00AB9B'
            }
          },
          '& .MuiInputLabel-outlined': {
            backgroundColor: '#FFFFFF',
            paddingLeft: '4px',
            paddingRight: '4px',
          }
        },
        '& .MuiTypography-root': {
          color: '#101D3B'
        },
        '& .MuiTypography-h1, & .MuiTypography-h2, & .MuiTypography-h3, & .MuiTypography-h4, & .MuiTypography-h5, & .MuiTypography-h6': {
          fontWeight: 700
        }
      }}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Registration />} />
          <Route path="/" element={
            <ProtectedRoute authResult={clientAuthResult}>
              <Home />
            </ProtectedRoute>
          } />
          <Route path="/clients/:clientId/requests" element={
            <ProtectedRoute authResult={clientAuthResult}>
              <ServiceRequests />
            </ProtectedRoute>
          } />
          <Route path="/clients/:clientId/requests/new" element={
            <ProtectedRoute authResult={clientAuthResult}>
              <NewServiceRequest />
            </ProtectedRoute>
          } />
          <Route path="/clients/:clientId/requests/:requestId" element={
            <ProtectedRoute authResult={clientAuthResult}>
              <ServiceRequestDetail />
            </ProtectedRoute>
          } />
        </Routes>
      </Container>
      
      {/* Property Drawer */}
      <PropertyDrawer 
        open={drawerOpen}
        onClose={closeDrawer}
      />
      
      {/* Bottom Navigation */}
      {currentUser && <BottomNavigation />}
    </>
  );
});

function App() {
  return (
    <div className="App">
      <NotificationProvider>
        <ClientContextProvider>
          <AppContent />
        </ClientContextProvider>
      </NotificationProvider>
    </div>
  );
}

export default App; 